<template>
  <div class="held" :class="{ 'held--embedded': embedded }">
    <div v-if="embedded" class="held__bar">
      <BaseButton type="outline" is-small @click="$emit('close')">← {{ backLabel || $t('buttons.back') }}</BaseButton>
      <span v-if="rows.length" class="held__title">{{ $t('carriers.import.held.Title', { count: formatCount(rows.length) }) }}</span>
    </div>

    <BasePreloader v-if="loading && !rows.length" color="purple" size="l"/>

    <!-- A failed load is not "nothing to decide", and rows left from before
         it may already be decided: offer a retry instead of either. -->
    <p v-else-if="loadFailed" class="held__text">
      {{ $t('adminTitles.someError') }}
      <BaseButton type="outline" is-small :disabled="loading" @click="load">{{ $t('adminTitles.TryAgain') }}</BaseButton>
    </p>

    <p v-else-if="!rows.length" class="held__text">{{ $t('carriers.import.payments.AllSettled') }}</p>

    <template v-else>
      <p class="held__text">{{ $t('carriers.import.held.Lead', { carrier: carrierName }) }}</p>

      <div v-if="rows.length > 1" class="held__bulk">
        <BaseButton
          is-small
          :disabled="!!busy"
          :loading="busy === 'ACCEPT:all'"
          @click="resolve(allIds, 'ACCEPT', 'all')"
        >{{ $t('carriers.import.held.ApplyAll', { count: formatCount(rows.length) }) }}</BaseButton>
        <BaseButton
          is-small
          type="outline"
          :disabled="!!busy"
          :loading="busy === 'DISMISS:all'"
          @click="resolve(allIds, 'DISMISS', 'all')"
        >{{ $t('carriers.import.held.KeepAll', { count: formatCount(rows.length) }) }}</BaseButton>
      </div>
      <p v-if="notice" class="held__notice">{{ notice }}</p>

      <section v-for="row in rows" :key="row.id" class="held__policy">
        <p class="held__head">
          <a
            v-if="row.insurancePolicyId"
            :href="`/admin/policies/${row.insurancePolicyId}`"
            target="_blank"
            class="held__number held__number--link"
          >{{ row.policyNumber }}</a>
          <span v-else class="held__number">{{ row.policyNumber }}</span>
          <span v-if="row.clientName" class="held__client">{{ row.clientName }}</span>
        </p>

        <p v-for="(why, i) in reasonsOf(row)" :key="i" class="held__why">{{ whyText(why) }}</p>

        <p v-if="changesOf(row).length" class="held__text">
          {{ $t('carriers.import.held.IfApplied', { changes: changesText(row) }) }}
          <button class="held__link" type="button" @click="toggleLines(row.id)">
            {{ open[row.id] ? $t('carriers.import.held.HidePayments') : $t('carriers.import.held.ShowPayments') }}
          </button>
        </p>
        <button
          v-else
          class="held__link"
          type="button"
          @click="toggleLines(row.id)"
        >
          {{ open[row.id] ? $t('carriers.import.held.HidePayments') : $t('carriers.import.held.ShowPayments') }}
        </button>

        <PaymentDiffTable v-if="open[row.id]" :pairs="pairsOf(row)"/>

        <p v-if="failures[row.id]" class="held__failure">{{ failures[row.id] }}</p>

        <div class="held__actions">
          <BaseButton
            is-small
            :disabled="!!busy"
            :loading="busy === `ACCEPT:${row.id}`"
            @click="resolve([row.id], 'ACCEPT', row.id)"
          >{{ $t('carriers.import.held.Apply', { carrier: carrierName }) }}</BaseButton>
          <BaseButton
            is-small
            type="outline"
            :disabled="!!busy"
            :loading="busy === `DISMISS:${row.id}`"
            @click="resolve([row.id], 'DISMISS', row.id)"
          >{{ $t('carriers.import.held.Keep') }}</BaseButton>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import BaseButton from '@/views/RenovaDashboard/components/ui-kit/Buttons/BaseButton';
import PaymentDiffTable from '@/views/RenovaDashboard/PaymentsParts/ImportHistory/PaymentDiffTable';
import { scheduleEntry } from '@/utils/paymentDiff';
import BasePreloader from '@/views/RenovaDashboard/components/ui-kit/Buttons/BasePreloader';
import { apiError, formatCount } from '@/utils/carrierImport';
import { formatCarrierDate } from '@/utils/carrierFormat';
import {
  SYNC_SCHEDULE, heldChanges, heldReasons, scheduleLines,
} from '@/utils/carrierPayments';

const PAGE = 200;
/** Each accept rebuilds a whole schedule; a few at a time keeps a request short. */
const COMMIT_CHUNK = 25;

/**
 * AXA policies the automatic payments update left for a person: the carrier's
 * listing had a row for the policy that could not be read (applying the rest
 * could delete or un-pay an instalment), or the update itself failed.
 *
 * A small inline list, not a review screen: per policy, why it waits, what
 * applying would do, the payments before and after on request, and two buttons.
 * Apply rebuilds the schedule from the receipts that could be read, as an
 * undoable run in Payments → Import history; Keep leaves it as it is.
 */
export default {
  name: 'CarrierHeldSchedules',
  components: { BaseButton, BasePreloader, PaymentDiffTable },
  props: {
    /** The payments run, with its `scrapeJobId` and, for the words, its `carrierName`. */
    run: { type: Object, required: true },
    /** In a drawer's place, with a Back to it. */
    embedded: { type: Boolean, default: false },
    backLabel: { type: String, default: '' },
  },
  emits: ['resolved', 'close'],
  data() {
    return {
      rows: [],
      loading: true,
      loadFailed: false,
      /** `${decision}:${row id | 'all'}` while a press is in flight. */
      busy: '',
      notice: '',
      /** Row id -> why the last decision on it failed; the row stays. */
      failures: {},
      /** Row id -> true while its payments are shown. */
      open: {},
    };
  },
  computed: {
    carrierName() {
      return this.run.carrierName || this.$t('carriers.import.TheCarrier');
    },
    allIds() {
      return this.rows.map((r) => r.id);
    },
  },
  watch: {
    'run.scrapeJobId': function reload() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    formatCount,
    fetchRows(page = 0, acc = []) {
      return this.$store.dispatch('carrierImportFetchPaymentRows', {
        scrapeJobId: this.run.scrapeJobId,
        reviewStatuses: ['PENDING'],
        groups: [SYNC_SCHEDULE],
        limit: PAGE,
        page,
      }).then((data) => {
        const items = (data && data.items) || [];
        const rows = acc.concat(items);
        const total = (data && data.totalCount) || rows.length;
        if (items.length === PAGE && rows.length < total) return this.fetchRows(page + 1, rows);
        return rows;
      });
    },
    load() {
      this.loading = true;
      this.loadFailed = false;
      return this.fetchRows()
        .then((rows) => { this.rows = rows; })
        .catch((err) => {
          this.loadFailed = true;
          this.$notify.error({ message: apiError(err, this.$t('adminTitles.someError')) });
        })
        .finally(() => { this.loading = false; });
    },
    reasonsOf(row) {
      return heldReasons(row.detail);
    },
    whyText(why) {
      if (why.kind === 'applyError') return this.$t('carriers.import.held.ApplyFailed', { error: why.message });
      if (why.kind === 'keptInRenova') {
        const key = `${why.status === 'CANCELED' ? 'KeptCanceled' : 'KeptPaid'}${why.receipt ? '' : 'Unlisted'}`;
        return this.$t(`carriers.import.held.${key}`, { date: formatCarrierDate(why.dueDate), carrier: this.carrierName });
      }
      const params = { row: why.row, carrier: this.carrierName, reason: why.reason };
      return why.reason
        ? this.$t('carriers.import.held.Unreadable', params)
        : this.$t('carriers.import.held.UnreadableNoReason', params);
    },
    changesOf(row) {
      return heldChanges(row.detail);
    },
    /** "2 payments marked paid · 1 payments removed". */
    changesText(row) {
      return this.changesOf(row)
        .map((piece) => `${formatCount(piece.count)} ${this.$t(piece.label)}`)
        .join(' · ');
    },
    /** Renova's instalments against what Apply would leave, as Payments → Import history shows a run. */
    pairsOf(row) {
      return scheduleLines(row.detail).map((line) => ({
        key: line.key,
        dueDate: line.dueDate,
        before: scheduleEntry(line.current),
        after: scheduleEntry(line.target),
      }));
    },
    toggleLines(id) {
      this.open = { ...this.open, [id]: !this.open[id] };
    },
    async resolve(ids, decision, target) {
      if (!ids.length || this.busy) return;
      this.busy = `${decision}:${target}`;
      this.notice = '';
      // One key per press; the chunks share it so the press is one entry in Import history.
      const idempotencyKey = `${this.run.scrapeJobId}:${SYNC_SCHEDULE}:${Date.now()}`;
      let done = 0;
      const failures = { ...this.failures };
      ids.forEach((id) => { delete failures[id]; });
      try {
        for (let i = 0; i < ids.length; i += COMMIT_CHUNK) {
          // Sequential on purpose: each accept rewrites schedules.
          // eslint-disable-next-line no-await-in-loop
          const out = await this.$store.dispatch('carrierImportResolvePayments', {
            ids: ids.slice(i, i + COMMIT_CHUNK), decision, idempotencyKey,
          });
          done += (out && (decision === 'ACCEPT' ? out.appliedCount : out.resolvedCount)) || 0;
          ((out && out.results) || []).filter((r) => r.status === 'FAILED').forEach((r) => {
            failures[r.id] = r.message || this.$t('adminTitles.someError');
          });
        }
      } catch (err) {
        this.$notify.error({ message: apiError(err, this.$t('adminTitles.someError')) });
      } finally {
        this.failures = failures;
        if (done) {
          this.notice = decision === 'ACCEPT'
            ? this.$t('carriers.import.held.Applied', { count: formatCount(done) })
            : this.$t('carriers.import.held.Kept', { count: formatCount(done) });
        }
        // Locked until the list is fresh: the rows on screen may already be decided.
        await this.load();
        this.busy = '';
        this.$emit('resolved');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.held {
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: default;

  &__bar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 4px;
  }

  &__title {
    font-weight: 600;
    color: var(--solid-black-purple);
  }

  &__text,
  &__why {
    font-size: 13px;
    line-height: 1.45;
    color: var(--solid-dark-purple);
  }

  &__why { color: var(--solid-black-purple-70); }

  &__bulk,
  &__actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__notice {
    font-size: 13px;
    color: var(--solid-black-purple);
  }

  &__policy {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 10px 0;
    border-top: 1px solid var(--solid-lavender-grey);
  }

  &__head {
    display: flex;
    align-items: baseline;
    gap: 10px;
    flex-wrap: wrap;
  }

  &__number { font-weight: 600; }

  &__client {
    font-size: 13px;
    color: var(--solid-dark-purple);
  }

  &__link {
    border: 0;
    background: none;
    padding: 0;
    cursor: pointer;
    color: var(--solid-purple);
    font-weight: 600;
    font-size: 13px;
  }

  // Opens the policy in a new tab, as Payments → Import history does.
  &__number--link {
    color: var(--solid-purple);
    text-decoration: none;

    &:hover { text-decoration: underline; }
  }

  &__failure {
    font-size: 13px;
    color: var(--solid-red, #c0392b);
  }
}
</style>
